import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FrameSourcePicker } from '@/components/cover/FrameSourcePicker';
import type { CoverFrameSource } from '@/lib/cover/buildPreviewStillInputProps';
import { resolveFrameSourceSelection, type FrameCandidates } from '@/lib/cover/resolveFrameSourceSelection';

export type CoverFrameChooserDialogProps = {
  /** Both candidates, already settled by the caller. */
  candidates: FrameCandidates;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportAspect: number;
  /** Re-runs a failed candidate. */
  onRetry?: (source: CoverFrameSource) => void;
  /** Called with the chosen, already-rendered frame (a data URL). */
  onConfirm: (choice: { source: CoverFrameSource; dataUrl: string }) => void;
};

/** "Usar frame atual": both candidates for the current playhead, side by
 *  side, and the user picks one. Only shown when the two actually differ. */
export function CoverFrameChooserDialog({
  open,
  onOpenChange,
  exportAspect,
  candidates,
  onRetry,
  onConfirm,
}: CoverFrameChooserDialogProps) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<CoverFrameSource | null>(null);
  const selected = resolveFrameSourceSelection(chosen, candidates);
  const selectedCandidate = selected ? candidates[selected] : null;
  const bothFailed = selected === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="cover-frame-chooser">
        <DialogHeader>
          <DialogTitle>{t('editor.coverImage.frameSource.title')}</DialogTitle>
          <DialogDescription>{t('editor.coverImage.frameSource.description')}</DialogDescription>
        </DialogHeader>
        <FrameSourcePicker
          candidates={candidates}
          selected={selected}
          onSelect={setChosen}
          onRetry={onRetry}
          exportAspect={exportAspect}
        />
        {bothFailed && (
          <p role="alert" className="text-sm text-destructive">
            {t('editor.coverImage.captureError')}
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('editor.coverImage.frameSource.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="cover-frame-chooser-confirm"
            disabled={selectedCandidate?.status !== 'ready'}
            onClick={() => {
              if (selected && selectedCandidate?.status === 'ready') {
                onConfirm({ source: selected, dataUrl: selectedCandidate.dataUrl });
              }
            }}
          >
            {t('editor.coverImage.frameSource.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
